import React, { useState, useEffect, useRef } from 'react';
import {
    MapPin, Navigation, Truck, Clock, Map, Zap, Activity,
    AlertTriangle, CheckCircle, Phone, MessageSquare, Eye,
    Maximize2, Minimize2, Filter, RefreshCw, Settings,
    Play, Pause, SkipBack, SkipForward, Volume2, Search,
    Users, Globe, Thermometer, Fuel, Battery, Signal,
    Target, ArrowLeft, Share2, Download
} from 'lucide-react';

const TrackingPage = () => {
    const mapRef = useRef(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [isMapFullscreen, setIsMapFullscreen] = useState(false);
    const [showTrafficLayer, setShowTrafficLayer] = useState(true);
    const [trackingMode, setTrackingMode] = useState('live');
    const [mapError, setMapError] = useState(false);
    const [filters, setFilters] = useState({
        status: 'all',
        jobType: 'all',
        search: ''
    });

    // Mock real-time vehicle data
    const [vehicles, setVehicles] = useState([
        {
            id: 'VH001',
            number: 'MH-01-AB-1234',
            driver: 'Rajesh Kumar',
            phone: '+91-9876543210',
            jobId: 'JOB001',
            status: 'moving',
            position: {
                lat: 19.0760,
                lng: 72.8777,
                address: 'Near Lonavala Toll Plaza, Mumbai-Pune Highway'
            },
            speed: 65,
            heading: 95,
            lastUpdate: new Date(),
            route: {
                origin: 'Mumbai Port',
                destination: 'Pune Warehouse',
                progress: 65,
                eta: '2024-03-15T14:30:00',
                distance: { total: 185, completed: 120, remaining: 65 },
                polyline: [
                    { lat: 18.9386, lng: 72.8353 },
                    { lat: 19.0760, lng: 72.8777 },
                    { lat: 18.6298, lng: 73.7997 }
                ]
            },
            vehicle: {
                type: 'Truck',
                model: 'Tata 407',
                fuelLevel: 75,
                engineTemp: 92,
                batteryLevel: 88
            },
            alerts: [
                { type: 'info', message: 'Vehicle entered highway', time: '10 mins ago' }
            ],
            container: 'CONT123456',
            customer: 'Tata Motors Ltd'
        },
        {
            id: 'VH002',
            number: 'DL-02-XY-9876',
            driver: 'Suresh Yadav',
            phone: '+91-9876543211',
            jobId: 'JOB002',
            status: 'loading',
            position: {
                lat: 28.6139,
                lng: 77.2090,
                address: 'Delhi Warehouse, Sector 18, Gurgaon'
            },
            speed: 0,
            heading: 0,
            lastUpdate: new Date(Date.now() - 300000),
            route: {
                origin: 'Delhi Warehouse',
                destination: 'Customer Location',
                progress: 15,
                eta: '2024-03-15T18:00:00',
                distance: { total: 95, completed: 14, remaining: 81 },
                polyline: [
                    { lat: 28.6139, lng: 77.2090 },
                    { lat: 28.5355, lng: 77.3910 }
                ]
            },
            vehicle: {
                type: 'Trailer',
                model: 'Ashok Leyland',
                fuelLevel: 82,
                engineTemp: 88,
                batteryLevel: 95
            },
            alerts: [
                { type: 'warning', message: 'Loading time exceeding normal', time: '2 mins ago' }
            ],
            container: 'CONT789012',
            customer: 'Maruti Suzuki'
        },
        {
            id: 'VH003',
            number: 'TN-09-BC-5432',
            driver: 'Murugan S',
            phone: '+91-9876543212',
            jobId: 'JOB003',
            status: 'delayed',
            position: {
                lat: 12.9716,
                lng: 77.5946,
                address: 'Hosur Bypass, Bangalore Highway'
            },
            speed: 15,
            heading: 180,
            lastUpdate: new Date(Date.now() - 120000),
            route: {
                origin: 'Chennai Port',
                destination: 'Bangalore Warehouse',
                progress: 45,
                eta: '2024-03-15T20:00:00',
                distance: { total: 345, completed: 155, remaining: 190 },
                polyline: [
                    { lat: 13.0827, lng: 80.2707 },
                    { lat: 12.9716, lng: 77.5946 },
                    { lat: 12.9716, lng: 77.5946 }
                ]
            },
            vehicle: {
                type: 'Multi Axle',
                model: 'Volvo FH',
                fuelLevel: 45,
                engineTemp: 105,
                batteryLevel: 76
            },
            alerts: [
                { type: 'error', message: 'Heavy traffic causing delay', time: '15 mins ago' },
                { type: 'warning', message: 'Engine temperature high', time: '5 mins ago' }
            ],
            container: 'CONT345678',
            customer: 'Infosys Limited'
        },
        {
            id: 'VH004',
            number: 'WB-03-CD-7890',
            driver: 'Amit Ghosh',
            phone: '+91-9876543213',
            jobId: 'JOB004',
            status: 'completed',
            position: {
                lat: 22.5726,
                lng: 88.3639,
                address: 'Vendor Warehouse, Salt Lake, Kolkata'
            },
            speed: 0,
            heading: 0,
            lastUpdate: new Date(Date.now() - 3600000),
            route: {
                origin: 'Kolkata Port',
                destination: 'Vendor Warehouse',
                progress: 100,
                eta: '2024-03-14T16:00:00',
                distance: { total: 420, completed: 420, remaining: 0 },
                polyline: [
                    { lat: 22.5726, lng: 88.3639 },
                    { lat: 22.5726, lng: 88.3639 }
                ]
            },
            vehicle: {
                type: 'Truck',
                model: 'Mahindra Blazo',
                fuelLevel: 35,
                engineTemp: 85,
                batteryLevel: 92
            },
            alerts: [
                { type: 'success', message: 'Delivery completed successfully', time: '1 hour ago' }
            ],
            container: 'CONT901234',
            customer: 'Coal India Ltd'
        }
    ]);

    // Simulate real-time updates
    useEffect(() => {
        const interval = setInterval(() => {
            setVehicles(prevVehicles =>
                prevVehicles.map(vehicle => {
                    if (vehicle.status === 'moving') {
                        const newProgress = Math.min(vehicle.route.progress + 0.5, 100);
                        return {
                            ...vehicle,
                            route: {
                                ...vehicle.route,
                                progress: newProgress
                            },
                            lastUpdate: new Date(),
                            speed: 60 + Math.random() * 20
                        };
                    }
                    return vehicle;
                })
            );
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    // Filter vehicles
    const filteredVehicles = vehicles.filter(vehicle => {
        if (filters.status !== 'all' && vehicle.status !== filters.status) return false;
        if (filters.search && !vehicle.number.toLowerCase().includes(filters.search.toLowerCase()) &&
            !vehicle.driver.toLowerCase().includes(filters.search.toLowerCase())) return false;
        return true;
    });

    // UI Components
    const Card = ({ children, className = '' }) => (
        <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
            {children}
        </div>
    );

    const Button = ({ children, variant = 'primary', size = 'md', className = '', onClick, ...props }) => {
        const variants = {
            primary: 'bg-blue-600 text-white hover:bg-blue-700',
            secondary: 'bg-green-600 text-white hover:bg-green-700',
            outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50',
            ghost: 'text-gray-600 hover:bg-gray-100',
            link: 'text-blue-600 hover:text-blue-800 underline'
        };
        const sizes = {
            sm: 'px-3 py-1.5 text-sm',
            md: 'px-4 py-2',
            lg: 'px-6 py-3 text-lg'
        };

        return (
            <button
                className={`font-medium focus:outline-none transition-colors flex items-center justify-center rounded-lg ${variants[variant]} ${sizes[size]} ${className}`}
                onClick={onClick}
                {...props}
            >
                {children}
            </button>
        );
    };

    const Badge = ({ children, variant = 'default', size = 'md', className = '' }) => {
        const variants = {
            default: 'bg-gray-100 text-gray-800',
            success: 'bg-green-100 text-green-800',
            warning: 'bg-yellow-100 text-yellow-800',
            danger: 'bg-red-100 text-red-800',
            blue: 'bg-blue-100 text-blue-800'
        };
        const sizes = {
            sm: 'px-2 py-1 text-xs',
            md: 'px-2.5 py-0.5 text-sm'
        };

        return (
            <span className={`inline-flex items-center rounded-full font-medium ${variants[variant]} ${sizes[size]} ${className}`}>
                {children}
            </span>
        );
    };

    const Input = ({ label, icon: Icon, className = '', ...props }) => (
        <div className="space-y-1">
            {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
            <div className="relative">
                {Icon && <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />}
                <input
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${Icon ? 'pl-10' : ''} ${className}`}
                    {...props}
                />
            </div>
        </div>
    );

    const Select = ({ options = [], className = '', ...props }) => (
        <select
            className={`px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
            {...props}
        >
            {options.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
            ))}
        </select>
    );

    // Simplified Map Component
    // Google Maps Component
    const GoogleMap = () => {
        useEffect(() => {
            const initMap = () => {
                if (!window.google) {
                    setMapError(true);
                    return;
                }

                const map = new window.google.maps.Map(document.getElementById('google-map'), {
                    zoom: 6,
                    center: { lat: 20.5937, lng: 78.9629 },
                    mapTypeId: 'roadmap'
                });

                const infoWindow = new window.google.maps.InfoWindow();
                const bounds = new window.google.maps.LatLngBounds();

                // Add traffic layer
                if (showTrafficLayer) {
                    const trafficLayer = new window.google.maps.TrafficLayer();
                    trafficLayer.setMap(map);
                }

                // Add vehicle markers
                filteredVehicles.forEach((vehicle) => {
                    const marker = new window.google.maps.Marker({
                        position: vehicle.position,
                        map: map,
                        title: vehicle.number,
                        icon: {
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 10,
                            fillColor: vehicle.status === 'moving' ? '#10B981' :
                                vehicle.status === 'delayed' ? '#EF4444' :
                                    vehicle.status === 'loading' ? '#F59E0B' : '#6B7280',
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2
                        }
                    });

                    marker.addListener('click', () => {
                        setSelectedVehicle(vehicle);
                        infoWindow.setContent(`
                        <div style="padding: 10px;">
                            <h3>${vehicle.number}</h3>
                            <p>Driver: ${vehicle.driver}</p>
                            <p>Status: ${vehicle.status}</p>
                            <p>Speed: ${vehicle.speed} km/h</p>
                        </div>
                    `);
                        infoWindow.open(map, marker);
                    });

                    bounds.extend(vehicle.position);
                });

                if (filteredVehicles.length > 0) {
                    map.fitBounds(bounds);
                }
            };

            // Load Google Maps script
            if (!window.google) {
                const script = document.createElement('script');
                script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyDEtXDWbePaNDXrNFiNyGJ1DmwyrXxuVRg&libraries=geometry';
                script.onload = initMap;
                script.onerror = () => setMapError(true);
                document.head.appendChild(script);
            } else {
                initMap();
            }
        }, [filteredVehicles, showTrafficLayer]);

        return (
            <div className={`${isMapFullscreen ? 'fixed inset-0 z-50' : 'h-96'} relative`}>
                <div id="google-map" className="w-full h-full rounded-xl" />
                {mapError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl">
                        <div className="text-center">
                            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-600">Map unavailable</p>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const getStatusConfig = (status) => {
        const configs = {
            moving: { color: 'success', icon: Truck, label: 'Moving', bgColor: 'bg-green-100' },
            loading: { color: 'warning', icon: Clock, label: 'Loading', bgColor: 'bg-yellow-100' },
            delayed: { color: 'danger', icon: AlertTriangle, label: 'Delayed', bgColor: 'bg-red-100' },
            completed: { color: 'default', icon: CheckCircle, label: 'Completed', bgColor: 'bg-gray-100' },
            offline: { color: 'default', icon: Signal, label: 'Offline', bgColor: 'bg-gray-100' }
        };
        return configs[status] || configs.offline;
    };

    const getSignalStrength = (lastUpdate) => {
        const timeDiff = Date.now() - new Date(lastUpdate).getTime();
        if (timeDiff < 60000) return 'excellent';
        if (timeDiff < 300000) return 'good';
        if (timeDiff < 900000) return 'fair';
        return 'poor';
    };

    const VehicleListItem = ({ vehicle }) => {
        const statusConfig = getStatusConfig(vehicle.status);
        const StatusIcon = statusConfig.icon;
        const signalStrength = getSignalStrength(vehicle.lastUpdate);

        const signalColors = {
            excellent: 'text-green-500',
            good: 'text-yellow-500',
            fair: 'text-orange-500',
            poor: 'text-red-500'
        };

        return (
            <Card
                className={`p-4 cursor-pointer transition-all ${selectedVehicle?.id === vehicle.id ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:shadow-md'
                    }`}
                onClick={() => setSelectedVehicle(vehicle)}
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                        <div className={`p-2 rounded-lg ${statusConfig.bgColor} mr-3`}>
                            <StatusIcon className={`w-5 h-5 ${vehicle.status === 'moving' ? 'text-green-600' :
                                vehicle.status === 'delayed' ? 'text-red-600' :
                                    vehicle.status === 'loading' ? 'text-yellow-600' : 'text-gray-600'
                                }`} />
                        </div>
                        <div>
                            <h4 className="font-semibold text-gray-900">{vehicle.number}</h4>
                            <p className="text-sm text-gray-600">{vehicle.driver}</p>
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Signal className={`w-4 h-4 ${signalColors[signalStrength]}`} />
                        <Badge variant={statusConfig.color} size="sm">
                            {statusConfig.label}
                        </Badge>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span className="truncate">{vehicle.position.address}</span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center text-gray-600">
                            <Map className="w-4 h-4 mr-1" />
                            <span className="truncate">{vehicle.route.origin} → {vehicle.route.destination}</span>
                        </div>
                        <span className="text-gray-500">
                            {vehicle.status === 'moving' ? `${vehicle.speed} km/h` : 'Stationary'}
                        </span>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${vehicle.route.progress}%` }}
                                />
                            </div>
                            <span className="text-sm text-gray-600">{vehicle.route.progress}%</span>
                        </div>
                        <span className="text-xs text-gray-500">
                            ETA: {new Date(vehicle.route.eta).toLocaleTimeString()}
                        </span>
                    </div>

                    {vehicle.alerts.length > 0 && (
                        <div className="pt-2 border-t border-gray-100">
                            {vehicle.alerts.slice(0, 2).map((alert, index) => (
                                <div key={index} className={`text-xs p-2 rounded mb-1 ${alert.type === 'error' ? 'bg-red-50 text-red-700' :
                                    alert.type === 'warning' ? 'bg-yellow-50 text-yellow-700' :
                                        alert.type === 'success' ? 'bg-green-50 text-green-700' :
                                            'bg-blue-50 text-blue-700'
                                    }`}>
                                    {alert.message} • {alert.time}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    const VehicleDetails = ({ vehicle }) => {
        if (!vehicle) return null;

        const statusConfig = getStatusConfig(vehicle.status);
        const StatusIcon = statusConfig.icon;

        return (
            <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                        <div className={`p-3 rounded-xl ${statusConfig.bgColor} mr-4`}>
                            <StatusIcon className={`w-6 h-6 ${vehicle.status === 'moving' ? 'text-green-600' :
                                vehicle.status === 'delayed' ? 'text-red-600' :
                                    vehicle.status === 'loading' ? 'text-yellow-600' : 'text-gray-600'
                                }`} />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">{vehicle.number}</h3>
                            <p className="text-gray-600">{vehicle.vehicle.model} • {vehicle.vehicle.type}</p>
                        </div>
                    </div>
                    <Badge variant={statusConfig.color}>
                        {statusConfig.label}
                    </Badge>
                </div>

                {/* Driver Information */}
                <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Driver Information</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                    <Users className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900">{vehicle.driver}</p>
                                    <p className="text-sm text-gray-600">{vehicle.phone}</p>
                                </div>
                            </div>
                            <div className="flex space-x-2">
                                <Button variant="outline" size="sm" onClick={() => window.open(`tel:${vehicle.phone}`)}>
                                    <Phone className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" size="sm">
                                    <MessageSquare className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Job Information */}
                <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Job Information</h4>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-gray-600">Job ID:</span>
                            <Button
                                variant="link"
                                size="sm"
                                onClick={() => alert(`Viewing job ${vehicle.jobId}`)}
                            >
                                {vehicle.jobId}
                            </Button>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-600">Container:</span>
                            <span className="font-medium">{vehicle.container}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-600">Customer:</span>
                            <span className="font-medium">{vehicle.customer}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-600">Distance:</span>
                            <span className="font-medium">
                                {vehicle.route.distance.completed}km / {vehicle.route.distance.total}km
                            </span>
                        </div>
                    </div>
                </div>

                {/* Vehicle Diagnostics */}
                <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Vehicle Diagnostics</h4>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                            <Fuel className={`w-6 h-6 mx-auto mb-2 ${vehicle.vehicle.fuelLevel > 50 ? 'text-green-500' :
                                vehicle.vehicle.fuelLevel > 25 ? 'text-yellow-500' : 'text-red-500'
                                }`} />
                            <p className="text-sm text-gray-600">Fuel</p>
                            <p className="font-semibold">{vehicle.vehicle.fuelLevel}%</p>
                        </div>

                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                            <Thermometer className={`w-6 h-6 mx-auto mb-2 ${vehicle.vehicle.engineTemp < 100 ? 'text-green-500' :
                                vehicle.vehicle.engineTemp < 110 ? 'text-yellow-500' : 'text-red-500'
                                }`} />
                            <p className="text-sm text-gray-600">Engine</p>
                            <p className="font-semibold">{vehicle.vehicle.engineTemp}°C</p>
                        </div>

                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                            <Battery className={`w-6 h-6 mx-auto mb-2 ${vehicle.vehicle.batteryLevel > 70 ? 'text-green-500' :
                                vehicle.vehicle.batteryLevel > 30 ? 'text-yellow-500' : 'text-red-500'
                                }`} />
                            <p className="text-sm text-gray-600">Battery</p>
                            <p className="font-semibold">{vehicle.vehicle.batteryLevel}%</p>
                        </div>
                    </div>
                </div>

                {/* Current Location */}
                <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Current Location</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start">
                            <MapPin className="w-5 h-5 text-blue-500 mt-0.5 mr-3" />
                            <div className="flex-1">
                                <p className="font-medium text-gray-900">{vehicle.position.address}</p>
                                <p className="text-sm text-gray-600 mt-1">
                                    Lat: {vehicle.position.lat.toFixed(4)}, Lng: {vehicle.position.lng.toFixed(4)}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    Last updated: {vehicle.lastUpdate.toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Alerts */}
                {vehicle.alerts.length > 0 && (
                    <div>
                        <h4 className="font-medium text-gray-900 mb-3">Recent Alerts</h4>
                        <div className="space-y-2">
                            {vehicle.alerts.map((alert, index) => (
                                <div key={index} className={`p-3 rounded-lg border-l-4 ${alert.type === 'error' ? 'bg-red-50 border-red-400 text-red-700' :
                                    alert.type === 'warning' ? 'bg-yellow-50 border-yellow-400 text-yellow-700' :
                                        alert.type === 'success' ? 'bg-green-50 border-green-400 text-green-700' :
                                            'bg-blue-50 border-blue-400 text-blue-700'
                                    }`}>
                                    <div className="flex items-center justify-between">
                                        <p className="font-medium">{alert.message}</p>
                                        <span className="text-sm opacity-75">{alert.time}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Card>
        );
    };

    return (
        <div className="p-6 space-y-6">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center">
                    <Button variant="ghost" onClick={() => window.history.back()} className="mr-4">
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                            <MapPin className="w-8 h-8 mr-3 text-blue-600" />
                            Live Fleet Tracking
                            <div className="ml-3 flex items-center">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2" />
                                <span className="text-sm font-normal text-gray-600">Real-time Updates</span>
                            </div>
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Monitor your entire fleet in real-time with GPS tracking, alerts, and comprehensive analytics
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                    <Input
                        placeholder="Search vehicles..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        icon={Search}
                        className="w-64"
                    />

                    <Select
                        value={filters.status}
                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                        options={[
                            { value: 'all', label: 'All Status' },
                            { value: 'moving', label: 'Moving' },
                            { value: 'loading', label: 'Loading' },
                            { value: 'delayed', label: 'Delayed' },
                            { value: 'completed', label: 'Completed' }
                        ]}
                    />

                    <Button variant="outline" size="sm">
                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                    </Button>

                    <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Export
                    </Button>

                    <Button variant="outline" size="sm">
                        <Settings className="w-4 h-4 mr-2" />
                        Settings
                    </Button>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                    { label: 'Total Vehicles', value: vehicles.length, icon: Truck, color: 'blue' },
                    { label: 'Moving', value: vehicles.filter(v => v.status === 'moving').length, icon: Navigation, color: 'green' },
                    { label: 'Delayed', value: vehicles.filter(v => v.status === 'delayed').length, icon: AlertTriangle, color: 'red' },
                    { label: 'Completed', value: vehicles.filter(v => v.status === 'completed').length, icon: CheckCircle, color: 'gray' }
                ].map((stat, index) => {
                    const IconComponent = stat.icon;
                    return (
                        <Card key={index} className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                                    <p className="text-sm text-gray-600">{stat.label}</p>
                                </div>
                                <div className={`p-3 rounded-lg ${stat.color === 'blue' ? 'bg-blue-100' :
                                    stat.color === 'green' ? 'bg-green-100' :
                                        stat.color === 'red' ? 'bg-red-100' : 'bg-gray-100'
                                    }`}>
                                    <IconComponent className={`w-6 h-6 ${stat.color === 'blue' ? 'text-blue-600' :
                                        stat.color === 'green' ? 'text-green-600' :
                                            stat.color === 'red' ? 'text-red-600' : 'text-gray-600'
                                        }`} />
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Map */}
                <div className="lg:col-span-8">
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Live Map</h3>
                            <div className="flex items-center space-x-2">
                                <Badge variant="success" size="sm">
                                    {filteredVehicles.filter(v => v.status === 'moving').length} vehicles moving
                                </Badge>
                                <Button variant="outline" size="sm">
                                    <Eye className="w-4 h-4 mr-2" />
                                    View All
                                </Button>
                            </div>
                        </div>
                        <GoogleMap />
                    </Card>
                </div>

                {/* Vehicle List */}
                <div className="lg:col-span-4">
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Vehicles ({filteredVehicles.length})
                            </h3>
                            <Button variant="outline" size="sm">
                                <Filter className="w-4 h-4" />
                            </Button>
                        </div>
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                            {filteredVehicles.map((vehicle) => (
                                <VehicleListItem key={vehicle.id} vehicle={vehicle} />
                            ))}
                        </div>
                    </Card>
                </div>
            </div>

            {/* Vehicle Details Panel */}
            {selectedVehicle && (
                <VehicleDetails vehicle={selectedVehicle} />
            )}
        </div>
    );
};

export default TrackingPage;